/**
 * Wraps one remote avatar (inside the R3F scene):
 *  - renders nothing when either side blocked the other (mutual block);
 *  - adds an invisible, clickable column over the avatar and its name tag, so
 *    a click (desktop), tap (mobile) or controller-ray select (VR) opens that
 *    person's player card.
 *
 * The column's material is invisible (no draw call) but raycastable, and is
 * tagged `userData.portal` so FixedRetargetedAvatar's click-to-walk treats a
 * click on a person like a click on a portal: handled, don't walk there.
 */
import React, { useEffect, useRef } from 'react';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { useSafetyStore, hiddenIn } from '../../stores/safetyStore';
import { openPlayerCard } from './openSafety';

interface TransformLike { position: [number, number, number]; rotation: number }

/** Live hit columns, for the first-person (pointer-locked) centre click. */
export const playerHitTargets = new Map<string, { mesh: THREE.Mesh; userId: string | null; name: string }>();

const HIT_RADIUS = 0.34;
const HIT_HEIGHT = 2.15; // feet to just over the name tag (tag sits at 1.92 m)
const hitGeometry = new THREE.CylinderGeometry(HIT_RADIUS, HIT_RADIUS, HIT_HEIGHT, 10, 1);
hitGeometry.translate(0, HIT_HEIGHT / 2 + 0.05, 0);
const hitMaterial = new THREE.MeshBasicMaterial({ visible: false });

export const PlayerSafetyGate: React.FC<{
  playerKey: string;
  userId?: string | null;
  userName: string;
  transformRef: React.MutableRefObject<TransformLike>;
  children: React.ReactNode;
}> = ({ playerKey, userId = null, userName, transformRef, children }) => {
  const hidden = useSafetyStore((s) => hiddenIn(s, userId, playerKey));
  const inXR = useXR((s) => !!s.session);
  const hit = useRef<THREE.Mesh>(null);

  useFrame(() => {
    const m = hit.current;
    if (!m) return;
    const p = transformRef.current.position;
    m.position.set(p[0], p[1], p[2]);
  });

  useEffect(() => {
    const m = hit.current;
    if (!m || hidden) return;
    playerHitTargets.set(playerKey, { mesh: m, userId, name: userName });
    return () => { if (playerHitTargets.get(playerKey)?.mesh === m) playerHitTargets.delete(playerKey); };
  }, [playerKey, userId, userName, hidden]);

  if (hidden) return null;

  const onClick = (e: ThreeEvent<MouseEvent>) => {
    // Pointer-locked first-person clicks are aimed at the screen centre;
    // SafetyWorldHost handles those (the event's pointer position is stale).
    if (!inXR && document.pointerLockElement) return;
    e.stopPropagation();
    openPlayerCard({ userId, key: playerKey, name: userName }, { vr: inXR });
  };

  return (
    <>
      {children}
      <mesh
        ref={hit}
        name="gx-player-hit"
        geometry={hitGeometry}
        material={hitMaterial}
        userData={{ portal: true, gxPlayerKey: playerKey }}
        onClick={onClick}
        onPointerOver={() => { if (!inXR) document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { if (!inXR) document.body.style.cursor = ''; }}
      />
    </>
  );
};

export default PlayerSafetyGate;
